import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'react-toastify';

import shotService from '@services/shotService';
import { ShotType, SpeedLevel } from '@types/index';

interface ShotSummaryProps {
  playerId: string;
}

const ShotSummary: React.FC<ShotSummaryProps> = ({ playerId }) => {
  const [shotSummary, setShotSummary] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedShot, setSelectedShot] = useState<any>(null);
  const [trendsDialogOpen, setTrendsDialogOpen] = useState(false);
  const [trendsData, setTrendsData] = useState<any[]>([]);

  useEffect(() => {
    loadShotSummary();
  }, [playerId]);

  const loadShotSummary = async () => {
    try {
      setLoading(true);
      const summary = await shotService.getPlayerShotSummary(playerId);
      setShotSummary(summary);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load shot summary');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTrends = async (shotTypeId: string, shotTypeName: string) => {
    try {
      const trends = await shotService.getPlayerShotTrends(playerId, shotTypeId, 30);
      setTrendsData(trends);
      setSelectedShot({ id: shotTypeId, name: shotTypeName });
      setTrendsDialogOpen(true);
    } catch (err: any) {
      toast.error('Failed to load trends data');
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 3) return 'success';
    if (difficulty <= 6) return 'warning';
    return 'error';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Shot Performance Summary
      </Typography>

      <Grid container spacing={3}>
        {shotSummary.map((shot) => (
          <Grid item xs={12} md={6} lg={4} key={shot.shot_type_id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{shot.shot_type_name}</Typography>
                  <Chip
                    label={`Level ${shot.difficulty_rating}`}
                    color={getDifficultyColor(shot.difficulty_rating)}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Overall Score
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={shot.avg_score || 0}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      color={getScoreColor(shot.avg_score || 0)}
                    />
                    <Typography variant="body2" fontWeight="bold">
                      {shot.avg_score ? Math.round(shot.avg_score) : 0}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Total Attempts
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {shot.total_attempts || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Success Rate
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {shot.success_rate_percentage || 0}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Avg Accuracy
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {shot.avg_accuracy ? Math.round(shot.avg_accuracy) : 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Avg Speed
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {shot.avg_speed ? Math.round(shot.avg_speed) : 0}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    Last Practice: {shot.last_practice_date ? 
                      new Date(shot.last_practice_date).toLocaleDateString() : 'Never'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleViewTrends(shot.shot_type_id, shot.shot_type_name)}
                    disabled={!shot.total_evaluations}
                  >
                    View Trends
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trends Dialog */}
      <Dialog
        open={trendsDialogOpen}
        onClose={() => setTrendsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Performance Trends - {selectedShot?.name}
        </DialogTitle>
        <DialogContent>
          {trendsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="practice_date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Bar dataKey="avg_score" fill="#1976d2" name="Average Score" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography color="textSecondary" align="center">
              No trend data available for the selected period
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrendsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShotSummary;

